import { copyTextWithNotify } from "@/helpers/clipboard.helper";
import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import useFormatter, { formatSlackHandle } from "@/hooks/use-formatter.hook.ts";
import { useRecent } from "@/hooks/use-recent.hook.ts";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import { useCurrencies } from "@/queries/currencies.query.ts";
import { useEmploymentContractsSearchInline } from "@/queries/employment-contracts.query.ts";
import { useEmploymentTypesSearchInline } from "@/queries/employment-types.query.ts";
import { useCompensationChangeReasons } from "@/queries/use-compensation-change-reasons.query.ts";
import { useCompensationComponents } from "@/queries/use-compensation-components.query.ts";
import { useDepartmentsSearchInline } from "@/queries/use-departments.query.ts";
import { useDivisionsSearchInline } from "@/queries/use-divisitions.query.ts";
import { useJobProfilesSearchInline } from "@/queries/use-job-profiles.query.ts";
import { useLegalEntitiesSearchInline } from "@/queries/use-legal-entities.query.ts";
import { useCountries, useLocationsSearchInline } from "@/queries/use-locations.query.ts";
import { useOrganizationListByType } from "@/queries/use-organization-lists.query.ts";
import { useWorkPatternsSearchInline } from "@/queries/use-work-patterns.query.ts";
import { ApiAppConfigUIEmployeeProfileSection } from "@/types/config.types.ts";
import {
  ApiCustomFieldValue,
  ApiField,
  ApiFieldTypes,
  EmployeeEditFieldProps,
} from "@/types/custom-field.types.ts";
import { ApiEmployeeEmployment } from "@/types/employees/employee-employment.types.ts";
import { getEmployeeCompensationPerTypes } from "@/types/employees/employee-compensation.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import {
  ApiEmployee,
  ApiEmployeeLifecycleStatus,
  EmployeeGenders,
  LifecycleStatusTexts,
} from "@/types/employees/employee.types.ts";
import { getJobProfileTitle } from "@/types/job-profile.types.ts";
import { ApiOrganizationListType } from "@/types/organization-list.types.ts";
import { ApiRecentKey } from "@/types/recents/recents.types.ts";
import { EmployeeFieldPermission, PermissionType } from "@/types/role.types.ts";
import { isCompensationRollupField } from "@/utils/compensation-custom-field.utils.ts";
import {
  Box,
  ButtonIcon,
  FontAwesomeIcon,
  LinkTo,
  ListItem,
  SelectItem,
  Stack,
  Text,
  Title,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { toZonedTime } from "date-fns-tz";
import { ReactNode, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

export default function useEmployeeCustomFieldsHook(editFieldTypes: ApiFieldTypes[] = []) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { showInfo } = useToasts();
  const [now] = useState(new Date());
  const {
    buildFromPartialDate,
    renderDate,
    renderPartialDate,
    formatDurationPartialDate,
    renderEmployeeName,
    transformDateToPartialDate,
    renderRelativeDate,
    renderTime,
  } = useFormatter();
  const {
    getValueForField,
    getPermissionForField,
    getSchemaForField,
    getFieldByType,
    renderCustomFieldListItem,
    renderPendingListItem,
    renderPendingValueText,
    getFieldTitle,
  } = useCustomFieldsHook();
  const editsField = (...types: ApiFieldTypes[]) =>
    types.some((type) => editFieldTypes.includes(type));
  const { data: countries } = useCountries(editsField(ApiFieldTypes.employee_address_country));
  const { data: currencies, isLoading: isCurrenciesLoading } = useCurrencies();
  const { getRecentOptions } = useRecent(
    editsField(
      ApiFieldTypes.employee_compensation_currency,
      ApiFieldTypes.employee_additional_compensation_currency
    )
  );
  const departments = useDepartmentsSearchInline(
    editsField(ApiFieldTypes.employee_position_department)
  );
  const divisions = useDivisionsSearchInline(editsField(ApiFieldTypes.employee_position_division));
  const locations = useLocationsSearchInline(editsField(ApiFieldTypes.employee_position_location));
  const jobProfiles = useJobProfilesSearchInline(
    editsField(ApiFieldTypes.employee_position_job_profile)
  );
  const employmentTypes = useEmploymentTypesSearchInline(
    editsField(ApiFieldTypes.employee_employment_type)
  );
  const employmentContracts = useEmploymentContractsSearchInline(
    editsField(ApiFieldTypes.employee_employment_contract)
  );
  const workPatterns = useWorkPatternsSearchInline(
    editsField(ApiFieldTypes.employee_employment_work_pattern)
  );
  const legalEntities = useLegalEntitiesSearchInline(
    editsField(ApiFieldTypes.employee_employment_legal_entity)
  );
  const { data: compensationChangeReasons, isLoading: isCompensationChangeReasonsLoading } =
    useCompensationChangeReasons(editsField(ApiFieldTypes.employee_compensation_change_reason));
  const { data: positionChangeReasons, isLoading: isPositionChangeReasonsLoading } =
    useOrganizationListByType(
      ApiOrganizationListType.employee_position_change_reasons,
      editsField(ApiFieldTypes.employee_position_change_reason)
    );
  const { data: terminationLeaveTypes, isLoading: isTerminationLeaveTypesLoading } =
    useOrganizationListByType(
      ApiOrganizationListType.termination_leave_types,
      editsField(ApiFieldTypes.employee_termination_leave_type)
    );
  const { data: terminationReasons, isLoading: isTerminationReasonsLoading } =
    useOrganizationListByType(
      ApiOrganizationListType.termination_reasons,
      editsField(ApiFieldTypes.employee_termination_reason)
    );
  const { data: additionalCompensationTypes, isLoading: isAdditionalCompensationTypesLoading } =
    useCompensationComponents(
      editsField(ApiFieldTypes.employee_additional_compensation_compensation_type)
    );

  const getSchemaForEmployeeEditForm = (
    sections: ApiAppConfigUIEmployeeProfileSection[],
    employee: ApiEmployee
  ) => {
    let schemaFields: yup.ObjectShape = {};

    sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (employeeCustomFieldCanView(field, employee)) {
          schemaFields = { ...schemaFields, ...getSchemaForField(field) };
        }
      });
    });

    return schemaFields;
  };

  const getValuesForFormByEmployee = (
    sections: ApiAppConfigUIEmployeeProfileSection[],
    employee?: ApiEmployee
  ) => {
    let values: Record<string, unknown> = {};

    if (!employee) return values;

    sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (employee && field.type != ApiFieldTypes.custom) {
          switch (field.type) {
            case ApiFieldTypes.employee_first_name:
              values[field.slug] = employee.first_name;
              break;
            case ApiFieldTypes.employee_name_pronunciation:
              values[field.slug] = employee.name_pronunciation;
              break;
            case ApiFieldTypes.employee_last_name:
              values[field.slug] = employee.last_name;
              break;
            case ApiFieldTypes.employee_middle_name:
              values[field.slug] = employee.middle_name;
              break;
            case ApiFieldTypes.employee_phone:
              values[field.slug] = employee.phone;
              break;
            case ApiFieldTypes.employee_phone_personal:
              values[field.slug] = employee.phone_personal;
              break;
            case ApiFieldTypes.employee_email:
              values[field.slug] = employee.email;
              break;
            case ApiFieldTypes.employee_email_personal:
              values[field.slug] = employee.email_personal;
              break;
            case ApiFieldTypes.employee_number:
              values[field.slug] = employee.personnel_number;
              break;
            case ApiFieldTypes.employee_address_address_line_1:
              values[field.slug] = employee.address_1;
              break;
            case ApiFieldTypes.employee_address_address_line_2:
              values[field.slug] = employee.address_2;
              break;
            case ApiFieldTypes.employee_address_city:
              values[field.slug] = employee.city;
              break;
            case ApiFieldTypes.employee_address_zip_code:
              values[field.slug] = employee.zip;
              break;
            case ApiFieldTypes.employee_address_state:
              values[field.slug] = employee.state;
              break;
            case ApiFieldTypes.employee_address_country:
              values[field.slug] = employee.country ? [employee.country.id] : undefined;
              break;
            case ApiFieldTypes.employee_date_birth:
              values[field.slug] = employee.birth_date
                ? buildFromPartialDate(employee.birth_date)
                : undefined;
              break;
            case ApiFieldTypes.employee_gender:
              values[field.slug] = employee.gender ? [employee.gender] : undefined;
              break;
          }
        } else {
          values = { ...values, ...getValueForField(field, employee.custom_fields) };
        }
      });
    });

    return values;
  };

  const employeeCustomFieldCanView = (field: ApiField, employee: ApiEmployee) => {
    const type = getEmployeePermissionForField(field, employee);

    return type == PermissionType.view || type == PermissionType.edit;
  };

  const employeeCustomFieldCanEdit = (field: ApiField, employee: ApiEmployee) => {
    if (isCompensationRollupField(field.type)) {
      return false;
    }

    const type = getEmployeePermissionForField(field, employee);

    return type == PermissionType.edit;
  };

  const getEmployeePermissionForField = (
    field: ApiField,
    employee: ApiEmployee
  ): PermissionType => {
    if (field.type === ApiFieldTypes.custom) {
      return getPermissionForField(field, employee.custom_fields);
    } else {
      switch (field?.type) {
        case ApiFieldTypes.employee_first_name:
        case ApiFieldTypes.employee_last_name:
        case ApiFieldTypes.employee_middle_name:
          return employee.employee_field_permissions[EmployeeFieldPermission.employee_name];
        case ApiFieldTypes.employee_name_pronunciation:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_name_pronunciation
          ];
        case ApiFieldTypes.employee_phone:
          return employee.employee_field_permissions[EmployeeFieldPermission.employee_phone];
        case ApiFieldTypes.employee_phone_personal:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_phone_personal
          ];
        case ApiFieldTypes.employee_email:
          return employee.employee_field_permissions[EmployeeFieldPermission.employee_email];
        case ApiFieldTypes.employee_email_personal:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_email_personal
          ];
        case ApiFieldTypes.employee_slack:
          return employee.employee_field_permissions[EmployeeFieldPermission.employee_slack];
        case ApiFieldTypes.employee_number:
          return employee.employee_field_permissions[EmployeeFieldPermission.employee_number];
        case ApiFieldTypes.employee_address_address_line_1:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_address_address_line_1
          ];
        case ApiFieldTypes.employee_address_address_line_2:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_address_address_line_2
          ];
        case ApiFieldTypes.employee_address_city:
          return employee.employee_field_permissions[EmployeeFieldPermission.employee_address_city];
        case ApiFieldTypes.employee_address_zip_code:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_address_zip_code
          ];
        case ApiFieldTypes.employee_address_state:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_address_state
          ];
        case ApiFieldTypes.employee_address_country:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_address_country
          ];
        case ApiFieldTypes.employee_date_birth:
          return employee.employee_field_permissions[EmployeeFieldPermission.employee_date_birth];
        case ApiFieldTypes.employee_gender:
          return employee.employee_field_permissions[EmployeeFieldPermission.employee_gender];
        case ApiFieldTypes.employee_position_job_title:
        case ApiFieldTypes.employee_position_job_profile:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_position_job_title
          ];
        case ApiFieldTypes.employee_position_reporting_to:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_position_reporting_to
          ];
        case ApiFieldTypes.employee_position_department:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_position_department
          ];
        case ApiFieldTypes.employee_position_division:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_position_division
          ];
        case ApiFieldTypes.employee_position_location:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_position_location
          ];
        case ApiFieldTypes.employee_position_comment:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_position_comment
          ];
        case ApiFieldTypes.employee_position_change_reason:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.manage_employee_position
          ];
        case ApiFieldTypes.employee_employment_type:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_employment_employment_type
          ];
        case ApiFieldTypes.employee_employment_contract:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_employment_contract
          ];
        case ApiFieldTypes.employee_employment_work_pattern:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_employment_work_pattern
          ];
        case ApiFieldTypes.employee_employment_legal_entity:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_employment_legal_entity
          ];
        case ApiFieldTypes.employee_employment_probation_date_end:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_employment_probation
          ];
        case ApiFieldTypes.employee_employment_comment:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_employment_comment
          ];
        case ApiFieldTypes.employee_compensation_base_salary_gross:
        case ApiFieldTypes.employee_compensation_gross_total:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_compensation_base_salary_gross
          ];
        case ApiFieldTypes.employee_compensation_base_salary_net:
        case ApiFieldTypes.employee_compensation_net_total:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_compensation_base_salary_net
          ];
        case ApiFieldTypes.employee_compensation_total_cost:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_compensation_total_cost
          ];
        case ApiFieldTypes.employee_compensation_change_reason:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_compensation_change_reason
          ];
        case ApiFieldTypes.employee_compensation_comment:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_compensation_comment
          ];
        case ApiFieldTypes.employee_compensation_currency:
        case ApiFieldTypes.employee_compensation_per_type:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.manage_employee_compensation
          ];
        case ApiFieldTypes.employee_additional_compensation_compensation_type:
        case ApiFieldTypes.employee_additional_compensation_amount_gross:
        case ApiFieldTypes.employee_additional_compensation_amount_net:
        case ApiFieldTypes.employee_additional_compensation_currency:
        case ApiFieldTypes.employee_additional_compensation_description:
        case ApiFieldTypes.employee_additional_compensation_comment:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_additional_compensation
          ];
        case ApiFieldTypes.employee_termination_last_working_date:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_termination_last_working_date
          ];
        case ApiFieldTypes.employee_termination_leave_type:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_termination_leave_type
          ];
        case ApiFieldTypes.employee_termination_reason:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_termination_reason
          ];
        case ApiFieldTypes.employee_termination_replacement_employee:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_termination_replacement_employee
          ];
        case ApiFieldTypes.employee_termination_eligible_for_rehire:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_termination_eligible_for_rehire
          ];
        case ApiFieldTypes.employee_termination_comment:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_termination_comment
          ];
        case ApiFieldTypes.employee_lifecycle_status:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_lifecycle_status
          ];
        case ApiFieldTypes.employee_last_activity_date:
          return employee.employee_field_permissions[
            EmployeeFieldPermission.employee_last_activity_date
          ];
      }

      return PermissionType.none;
    }
  };

  const getEmployeeEditFieldByType = (
    field: ApiField,
    employee: ApiEmployee,
    formHook: UseFormReturn<Record<string, unknown>>,
    props: EmployeeEditFieldProps = {}
  ) => {
    let preFieldSlot: ReactNode | undefined;
    let options: SelectItem[] | undefined;
    let recentOptions: SelectItem[] | undefined;
    let searchOptions: SelectItem[] | undefined;
    let onSearchOptions: ((text: string | undefined) => void) | undefined;
    let isLoading: boolean | undefined;
    let loadOptionsOnOpen: boolean | undefined;

    switch (field.type) {
      case ApiFieldTypes.employee_address_country:
        options = countries ?? [];
        break;
      case ApiFieldTypes.employee_gender:
        options = EmployeeGenders.map((item) => ({
          id: item.id,
          title: t(item.title),
        }));
        break;
      case ApiFieldTypes.employee_position_department:
        options = departments.data ?? [];
        searchOptions = departments.options;
        onSearchOptions = departments.search;
        isLoading = departments.isLoading;
        break;
      case ApiFieldTypes.employee_position_division:
        options =
          employee.employee_position?.division && !employee.employee_position.division.is_archived
            ? [
                {
                  id: employee.employee_position.division.id,
                  title: employee.employee_position.division.title,
                },
              ]
            : [];
        searchOptions = divisions.options;
        onSearchOptions = divisions.search;
        isLoading = divisions.isLoading;
        loadOptionsOnOpen = true;
        break;
      case ApiFieldTypes.employee_position_job_profile:
        options =
          employee.employee_position?.job_profile &&
          !employee.employee_position.job_profile.is_archived
            ? [
                {
                  id: employee.employee_position.job_profile.id,
                  title: getJobProfileTitle(employee.employee_position.job_profile),
                },
              ]
            : [];
        searchOptions = jobProfiles.options;
        onSearchOptions = jobProfiles.search;
        isLoading = jobProfiles.isLoading;
        loadOptionsOnOpen = true;
        break;
      case ApiFieldTypes.employee_position_location:
        options = locations.data ?? [];
        searchOptions = locations.options;
        onSearchOptions = locations.search;
        isLoading = locations.isLoading;
        break;
      case ApiFieldTypes.employee_position_change_reason:
        options = (positionChangeReasons?.items ?? [])
          .filter((item) => !item.is_archived)
          .map((item) => ({ id: item.id, title: item.title }));
        isLoading = isPositionChangeReasonsLoading;
        break;
      case ApiFieldTypes.employee_termination_leave_type:
        options = (terminationLeaveTypes?.items ?? [])
          .filter((item) => !item.is_archived)
          .map((item) => ({ id: item.id, title: item.title }));
        isLoading = isTerminationLeaveTypesLoading;
        break;
      case ApiFieldTypes.employee_termination_reason:
        options = (terminationReasons?.items ?? [])
          .filter((item) => !item.is_archived)
          .map((item) => ({ id: item.id, title: item.title }));
        isLoading = isTerminationReasonsLoading;
        break;
      case ApiFieldTypes.employee_employment_type:
        options = employmentTypes.data ?? [];
        searchOptions = employmentTypes.options;
        onSearchOptions = employmentTypes.search;
        isLoading = employmentTypes.isLoading;
        break;
      case ApiFieldTypes.employee_employment_contract:
        options = employmentContracts.data ?? [];
        searchOptions = employmentContracts.options;
        onSearchOptions = employmentContracts.search;
        isLoading = employmentContracts.isLoading;
        break;
      case ApiFieldTypes.employee_employment_work_pattern:
        options = workPatterns.data ?? [];
        searchOptions = workPatterns.options;
        onSearchOptions = workPatterns.search;
        isLoading = workPatterns.isLoading;
        break;
      case ApiFieldTypes.employee_employment_legal_entity:
        options = legalEntities.data ?? [];
        searchOptions = legalEntities.options;
        onSearchOptions = legalEntities.search;
        isLoading = legalEntities.isLoading;
        break;
      case ApiFieldTypes.employee_compensation_per_type:
        options = getEmployeeCompensationPerTypes();
        break;
      case ApiFieldTypes.employee_compensation_currency:
      case ApiFieldTypes.employee_additional_compensation_currency:
        options =
          currencies?.map((currency) => ({ id: currency.code, title: currency.code })) ?? [];
        recentOptions = getRecentOptions(ApiRecentKey.currency, options);
        isLoading = isCurrenciesLoading;
        break;
      case ApiFieldTypes.employee_compensation_change_reason:
        options = (compensationChangeReasons ?? [])
          .filter((item) => !item.is_archived)
          .map((item) => ({ id: item.id, title: item.title }));
        isLoading = isCompensationChangeReasonsLoading;
        break;
      case ApiFieldTypes.employee_additional_compensation_compensation_type:
        options = (additionalCompensationTypes ?? [])
          .filter((item) => !item.is_archived)
          .map((item) => ({ id: item.id, title: item.title }));
        isLoading = isAdditionalCompensationTypesLoading;
        break;
    }

    if (field.type == ApiFieldTypes.employee_email) {
      preFieldSlot =
        !employee.datetime_invited_at && !employee.datetime_last_online ? (
          <Box className={"mt-4 mb-1 border rounded-xs bg-dark/5 border-dark/5 p-3 text-sm"}>
            {t("core:new_employee_manual_invitation_description")}
          </Box>
        ) : undefined;
    }

    const fieldValue =
      field.type === ApiFieldTypes.employee_position_reporting_to &&
      employee.employee_position?.manager
        ? {
            value_int_array: [employee.employee_position.manager.id],
            value_employees: [employee.employee_position.manager],
          }
        : employee.custom_fields.find((wrapperField) => wrapperField.field.id == field.id)?.value;

    return getFieldByType({
      preFieldSlot,
      field,
      formHook,
      canView: props.forceCanView ?? employeeCustomFieldCanView(field, employee),
      canEdit: props.forceCanEdit ?? employeeCustomFieldCanEdit(field, employee),
      fieldValue: fieldValue ?? undefined,
      options,
      recentOptions,
      searchOptions,
      onSearchOptions,
      isLoading,
      loadOptionsOnOpen,
    });
  };

  const renderBuiltinPendingValue = (
    field: ApiField,
    value: ApiCustomFieldValue | null
  ): ReactNode => {
    if (!value) return "-";
    if (field.type === ApiFieldTypes.employee_gender) {
      const gender = EmployeeGenders.find((item) => item.id === value.value_text);
      return gender ? t(gender.title) : "-";
    }
    if (field.type === ApiFieldTypes.employee_address_country) {
      return countries?.find((item) => item.id === value.value_int)?.title ?? "-";
    }
    return renderPendingValueText(field, value);
  };

  const renderEmployeeCustomFieldListItem = (
    field: ApiField,
    employee: ApiEmployee,
    openLinksInNewTab?: boolean
  ): ReactNode | undefined => {
    const handleEmployeeLinkClick = (event: React.MouseEvent, employeeId: number) => {
      if (openLinksInNewTab) {
        return;
      }

      if (event.metaKey || event.ctrlKey) {
        return;
      }

      event.preventDefault();
      openModal(EmployeeViewModal, {
        employeeId,
      });
    };

    let item: ReactNode | undefined;

    const pendingChange =
      field.type == ApiFieldTypes.custom
        ? employee.pending_changes?.find(
            (pc) => pc.field.type === ApiFieldTypes.custom && pc.field.id === field.id
          )
        : employee.pending_changes?.find((pc) => pc.field.type === field.type);

    if (field.type == ApiFieldTypes.custom) {
      item = renderCustomFieldListItem(field, employee.custom_fields, pendingChange);
    } else {
      let title: string | undefined;
      let caption: string | undefined;
      let valueSlot: ReactNode | undefined;
      let onClick: (() => void) | undefined;
      let truncateTitle = false;
      const employeePosition = employee.employee_position;
      const employeeEmployment = employee.employee_employment;

      switch (field.type) {
        case ApiFieldTypes.employee_name_pronunciation:
          if (employee.name_pronunciation) {
            title = employee.name_pronunciation;
          }
          break;
        case ApiFieldTypes.employee_phone:
          if (employee.phone) {
            title = employee.phone;
          }
          break;
        case ApiFieldTypes.employee_phone_personal:
          if (employee.phone_personal) {
            title = employee.phone_personal;
          }
          break;
        case ApiFieldTypes.employee_email:
          if (employee.email) {
            const email = employee.email;
            title = email;
            truncateTitle = true;
            valueSlot = (
              <ButtonIcon icon={<FontAwesomeIcon icon={"fa-light fa-copy"} />} variant={"plain"} />
            );
            onClick = () => {
              copyTextWithNotify(email).then((copied) => {
                if (!copied) return;
                showInfo({ title: t("core:work_email_copied") });
              });
            };
          }
          break;
        case ApiFieldTypes.employee_email_personal:
          if (employee.email_personal) {
            const email = employee.email_personal;
            title = email;
            truncateTitle = true;
            valueSlot = (
              <ButtonIcon icon={<FontAwesomeIcon icon={"fa-light fa-copy"} />} variant={"plain"} />
            );
            onClick = () => {
              copyTextWithNotify(email).then((copied) => {
                if (!copied) return;
                showInfo({ title: t("core:email_personal_copied") });
              });
            };
          }
          break;
        case ApiFieldTypes.employee_slack:
          if (employee.slack) {
            const slack = employee.slack;
            item = (
              <ListItem
                preTitle={getFieldTitle(field)}
                titleSlot={
                  <LinkTo to={slack.url} target={"_blank"} variant={"plain"}>
                    <ListItem
                      preTitle={slack.name}
                      caption={formatSlackHandle(slack.handle)}
                      avatarUrl={slack.avatar_url ?? undefined}
                      behavior={"card"}
                      emphasis={"top"}
                    />
                  </LinkTo>
                }
              />
            );
          }
          break;
        case ApiFieldTypes.employee_number:
          if (employee.personnel_number) {
            title = employee.personnel_number;
          }
          break;
        case ApiFieldTypes.employee_address_address_line_1:
          if (employee.address_1) {
            title = employee.address_1;
          }
          break;
        case ApiFieldTypes.employee_address_address_line_2:
          if (employee.address_2) {
            title = employee.address_2;
          }
          break;
        case ApiFieldTypes.employee_address_city:
          if (employee.city) {
            title = employee.city;
          }
          break;
        case ApiFieldTypes.employee_address_zip_code:
          if (employee.zip) {
            title = employee.zip;
          }
          break;
        case ApiFieldTypes.employee_address_state:
          if (employee.state) {
            title = employee.state;
          }
          break;
        case ApiFieldTypes.employee_address_country:
          if (employee.country) {
            title = employee.country.title;
          }
          break;
        case ApiFieldTypes.employee_date_birth:
          if (employee.birth_date) {
            title = renderPartialDate(employee.birth_date);

            if (employee.birth_date.year) {
              caption = formatDurationPartialDate(employee.birth_date, undefined, true);
            }
          }
          break;
        case ApiFieldTypes.employee_gender:
          if (employee.gender) {
            title = t(EmployeeGenders.find((gender) => gender.id == employee.gender)?.title ?? "");
          }
          break;
        case ApiFieldTypes.employee_position_department:
          if (employeePosition?.department) {
            title = employeePosition.department.title;
          }
          break;
        case ApiFieldTypes.employee_employment_legal_entity:
          if (employeeEmployment?.legal_entity) {
            title = employeeEmployment.legal_entity.title;
          }
          break;
        case ApiFieldTypes.employee_position_division:
          if (employeePosition?.division) {
            title = employeePosition.division.title;
          }
          break;
        case ApiFieldTypes.employee_position_location:
          if (employeePosition?.location) {
            title = employeePosition.location.title;
          }
          break;
        case ApiFieldTypes.employee_hired_on:
          if (employee.date_hired_on) {
            title = renderDate(employee.date_hired_on);
            const isTerminated =
              employee.lifecycle_status === ApiEmployeeLifecycleStatus.terminated;
            if (employee.date_hired_on < new Date()) {
              caption = formatDurationPartialDate(
                transformDateToPartialDate(employee.date_hired_on),
                isTerminated && employee.date_termination
                  ? transformDateToPartialDate(employee.date_termination)
                  : undefined,
                false,
                true
              );
            }
          }
          break;
        case ApiFieldTypes.employee_employment_probation_date_end:
          if (employeeEmployment?.date_probation_ends) {
            title = renderDate(employeeEmployment.date_probation_ends);
            const probationEndInclusive = new Date(employeeEmployment.date_probation_ends);
            probationEndInclusive.setDate(probationEndInclusive.getDate() + 1);
            caption =
              employeeEmployment.date_effective_from <= employeeEmployment.date_probation_ends
                ? formatDurationPartialDate(
                    transformDateToPartialDate(employeeEmployment.date_effective_from),
                    transformDateToPartialDate(probationEndInclusive),
                    false,
                    false
                  )
                : "";
          }
          break;
        case ApiFieldTypes.employee_timezone:
          if (employee.timezone) {
            const zonedTime = toZonedTime(now, employee.timezone);
            title = renderTime(zonedTime);
            caption = employee.timezone;
          }
          break;
        case ApiFieldTypes.employee_lifecycle_status: {
          if (!employee.lifecycle_status) break;

          const employment = employeeEmployment as ApiEmployeeEmployment | null | undefined;
          const isEmployed = employee.lifecycle_status === ApiEmployeeLifecycleStatus.employed;
          const isOnLeave =
            employee.lifecycle_status === ApiEmployeeLifecycleStatus.on_leave ||
            employee.lifecycle_status === ApiEmployeeLifecycleStatus.on_parental_leave;
          const isActive = isEmployed || isOnLeave;
          const hasTerminationPlanned =
            isActive &&
            !!employment?.date_effective_to &&
            new Date(employment.date_effective_to) > now;

          const statusText = LifecycleStatusTexts()[employee.lifecycle_status];

          const statusColorClass = hasTerminationPlanned
            ? "text-warning"
            : isEmployed || isOnLeave
              ? "text-positive"
              : undefined;

          item = (
            <ListItem
              preTitle={t("core:lifecycle_status")}
              titleSlot={
                <Text
                  className={twMerge(
                    "font-semibold",
                    statusColorClass ? statusColorClass : undefined
                  )}
                >
                  {statusText}
                </Text>
              }
              valueSlot={valueSlot}
            />
          );
          break;
        }
        case ApiFieldTypes.employee_last_activity_date:
          if (employee.datetime_last_online) {
            title = renderRelativeDate(employee.datetime_last_online);
          }
          break;
        case ApiFieldTypes.employee_employment_termination_date:
          if (employee.date_termination) {
            title = renderDate(employee.date_termination);
          }
          break;
        case ApiFieldTypes.employee_position_reporting_to: {
          const manager = employeePosition?.manager;
          const departmentManager = employeePosition?.department_manager;

          item = (
            <Stack className={"w-full"}>
              {manager ? (
                <>
                  <Link
                    to={`/employees/${manager.id}`}
                    target={openLinksInNewTab ? "_blank" : undefined}
                    rel={openLinksInNewTab ? "noopener noreferrer" : undefined}
                    className={"pt-2"}
                    onClick={(event) => handleEmployeeLinkClick(event, manager.id)}
                  >
                    <ListItem
                      preTitle={renderEmployeeName(manager)}
                      title={getEmployeePositionTitle(manager)}
                      icon={<EmployeeAvatar employee={manager} size={"md"} />}
                      emphasis={"top"}
                      behavior={"card"}
                      valueSlot={<FontAwesomeIcon icon={"fa-light fa-chevron-right"} />}
                    />
                  </Link>
                </>
              ) : (
                <ListItem title={"-"} forceShowAvatar={true} behavior={"card"} />
              )}
              {departmentManager && (
                <>
                  <Title
                    header={t("core:department_lead")}
                    size={"xs"}
                    variant={"secondary"}
                    paddingTop={true}
                  />
                  <Link
                    to={`/employees/${departmentManager.employee.id}`}
                    target={openLinksInNewTab ? "_blank" : undefined}
                    rel={openLinksInNewTab ? "noopener noreferrer" : undefined}
                    className={"pt-2"}
                    onClick={(event) =>
                      handleEmployeeLinkClick(event, departmentManager.employee.id)
                    }
                  >
                    <ListItem
                      preTitle={renderEmployeeName(departmentManager.employee)}
                      title={getEmployeePositionTitle(departmentManager.employee)}
                      caption={
                        departmentManager.department?.title
                          ? `${t("core:department_lead_prefix")} ${departmentManager.department.title}`
                          : undefined
                      }
                      icon={
                        <EmployeeAvatar
                          employee={departmentManager.employee}
                          size={"md"}
                          subAvatar={{
                            emoji: departmentManager.department?.emoji,
                            acronym: departmentManager.department?.title,
                          }}
                        />
                      }
                      emphasis={"top"}
                      behavior={"card"}
                      valueSlot={<FontAwesomeIcon icon={"fa-light fa-chevron-right"} />}
                    />
                  </Link>
                </>
              )}
            </Stack>
          );

          break;
        }
        case ApiFieldTypes.employee_position_job_title:
          if (employeePosition?.job_profile) {
            title = getJobProfileTitle(employeePosition.job_profile);
          }
          break;
      }

      if (pendingChange && !item) {
        const newValue = renderBuiltinPendingValue(field, pendingChange.value_after);
        item = renderPendingListItem(
          getFieldTitle(field),
          newValue,
          title ?? "-",
          newValue,
          pendingChange.datetime_created_at
        );
      } else if (title) {
        item = (
          <ListItem
            preTitle={getFieldTitle(field)}
            title={title}
            caption={caption}
            valueSlot={valueSlot}
            onClick={onClick}
            truncate={truncateTitle}
          />
        );
      }
    }
    return item;
  };

  return {
    getValuesForFormByEmployee,
    employeeCustomFieldCanView,
    employeeCustomFieldCanEdit,
    getEmployeePermissionForField,
    getSchemaForEmployeeEditForm,
    getEmployeeEditFieldByType,
    renderEmployeeCustomFieldListItem,
  };
}
